'use client';

import type { StoreApi, UseBoundStore } from 'zustand';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

export type ThesisTradeDirection = 'buy' | 'sell';

export interface ThesisTradeContext {
  /** 0 = NO, 1 = YES (matches existing PositionPill / SDK convention). */
  outcomeIndex: number;
  direction: ThesisTradeDirection;
  /** D6-encoded USDC amount as a BigInt-safe string. For buys: gross in. For sells: net out. */
  amountD6: string;
  /** Optional label resolved at the call site (e.g. "YES" / "NO"). May be null when unknown. */
  outcomeLabel: string | null;
}

interface ThesisDialogState {
  open: boolean;
  marketAddress: string | null;
  /** Relay signature (fallback — may differ from on-chain sig when Jito relay is used). */
  tradeSignature: string | null;
  /** Wallet address of the trader (used to match trade_events realtime events). */
  walletAddress: string | null;
  /** Authoritative signature reconciled after confirmation. Preferred over tradeSignature. */
  onChainSignature: string | null;
  /** Stable confirmation identity used to dedupe success and reconciliation callbacks. */
  tradeId: string | null;
  /** Trade context for the post-trade thesis dialog (pill, suggested prompt, slot ordering). */
  tradeContext: ThesisTradeContext | null;
  openDialog: (
    marketAddress: string,
    tradeSignature: string,
    walletAddress: string,
    tradeContext?: ThesisTradeContext | null,
    tradeId?: string,
  ) => void;
  closeDialog: () => void;
  /** Update the authoritative signature only while the matching dialog remains open. */
  reconcileDialogSignature: (tradeId: string, signature: string) => void;
}

export const useThesisDialogStore: UseBoundStore<StoreApi<ThesisDialogState>> =
  createTrackedStore<ThesisDialogState>('useThesisDialogStore')((set, get) => ({
    open: false,
    marketAddress: null,
    tradeSignature: null,
    walletAddress: null,
    onChainSignature: null,
    tradeId: null,
    tradeContext: null,
    openDialog(
      marketAddress: string,
      tradeSignature: string,
      walletAddress: string,
      tradeContext: ThesisTradeContext | null = null,
      tradeId: string = tradeSignature,
    ): void {
      const current = get();
      if (current.open && current.tradeId === tradeId) return;

      set({
        open: true,
        marketAddress,
        tradeSignature,
        walletAddress,
        onChainSignature: null,
        tradeId,
        tradeContext,
      });
    },
    closeDialog(): void {
      set({
        open: false,
        marketAddress: null,
        tradeSignature: null,
        walletAddress: null,
        onChainSignature: null,
        tradeId: null,
        tradeContext: null,
      });
    },
    reconcileDialogSignature(tradeId: string, signature: string): void {
      const current = get();
      if (!current.open || current.tradeId !== tradeId) return;

      set({ onChainSignature: signature });
    },
  }));
